import { heatLegendJackpots, OrderByEnum } from 'common/common';

// Jackpot insights only have 3 heat levels ([5,4,3]) and always show all of them.
export const JACKPOT_INSIGHTS = ['jackpot_value', 'jackpots', 'jackpot_value_per_spin', 'pop'];

/**
 * The heat filter that should be active for a given insight + sort direction.
 * Jackpots: all levels. Otherwise DESC = top 2 ([5,4]), ASC = bottom 2 ([2,1]).
 */
export const getHeatFilterForSortDirection = (insight: string, orderby: string): number[] => {
  if (JACKPOT_INSIGHTS.includes(insight)) return heatLegendJackpots;
  return orderby === OrderByEnum.Desc ? [5, 4] : [2, 1];
};

/**
 * Insight casinos only: which Hot/Cold heat filter to re-apply after a
 * sort/tab/data change, or null to do nothing.
 *
 * GetPlayDataReport replays the cached GetCasinoInsights response (shareReplay)
 * synchronously, so the loading flag never flips true -> false and MapStats'
 * post-load filter effect never fires on a tab switch. This re-applies the
 * same sort-direction filter on every change instead.
 *
 * Jackpot insights also get null: their filter is owned by the legacy
 * insight-change / post-load path, exactly as before the reset existed.
 *
 * Non-insight casinos always get null, so their legacy post-load path is the
 * only thing that sets the filter (unchanged behaviour).
 */
export const getInsightHeatFilterReset = (args: {
  isInsightCasino: boolean;
  isDataLoading: boolean;
  hasData: boolean;
  insight: string;
  orderby: string;
  getFilter?: (insight: string, orderby: string) => number[];
}): number[] | null => {
  if (!args.isInsightCasino || args.isDataLoading || !args.hasData) return null;
  // Jackpot insights keep their original (pre-reset) filter path untouched.
  if (JACKPOT_INSIGHTS.includes(args.insight)) return null;
  return (args.getFilter ?? getHeatFilterForSortDirection)(args.insight, args.orderby);
};

/**
 * Sort direction for a header click (tab change or same-column toggle).
 * - Same column: flip the direction (sort toggle).
 * - New column, insight casino: every insight tab opens sorted DESC (most), so
 *   its heat filter resets to the top 2 instead of inheriting Coldest's ASC.
 * - New column, non-insight: legacy behaviour (Coldest keeps ASC, else DESC).
 */
export const getOrderbyForSortChange = (args: {
  isInsightCasino: boolean;
  prevInsight: string;
  prevOrderby: string;
  nextInsight: string;
  activeShortcutId: string;
}): OrderByEnum => {
  if (args.prevInsight === args.nextInsight) {
    return args.prevOrderby === OrderByEnum.Asc ? OrderByEnum.Desc : OrderByEnum.Asc;
  }
  if (args.isInsightCasino) return OrderByEnum.Desc;
  return args.activeShortcutId === 'coldest' ? OrderByEnum.Asc : OrderByEnum.Desc;
};
